import { AppBar, Box, Chip, Slide, Stack, Toolbar, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { SdxColors } from '@sodexo/ui-components';
import { type PropsWithChildren, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import packageJson from '../../../package.json';
import { useStore } from '../../store/store';
import { Colors, themeVariables } from '../../theme';
import { ExcelDownloadProgress } from '../ExcelDownloadProgress';
import SearchBar from '../SearchBar';
import { GlobalCatalogSelector } from './GlobalCatalogSelector/GlobalCatalogSelector';
import { UserMenu } from './UserMenu/UserMenu';

const drawerWidth = 300;

export default function PersistentDrawerLeft({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const productSwapHomeLinkRef = useRef(null);
  const isTabletAndLower = useMediaQuery(theme.breakpoints.down('md'));
  const [catalog] = useStore(useShallow(state => [state.catalog]));
  const latestPriceUpdateDate =
    (catalog?.latest_price_update_date &&
      new Date(catalog?.latest_price_update_date).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })) ||
    '';

  const showDevTag = useMemo(() => !isTabletAndLower, [isTabletAndLower]);

  const subHeader = useMemo(() => {
    if (location.pathname.includes('/batch')) {
      return t('components.topBar.batchSubheader');
    }
    if (location.pathname.startsWith('/products') || location.pathname.startsWith('/swaps')) {
      return t('components.topBar.skuSubheader');
    }
    return null;
  }, [location.pathname]);

  return (
    <Stack direction='column'>
      <AppBarStyled position='fixed' elevation={0}>
        <Toolbar>
          <Stack direction='row' alignItems='center' gap={1} flex={1} height={48}>
            <NavLink ref={productSwapHomeLinkRef} to='/' style={{ textDecoration: 'none' }} data-testid='home-link'>
              <Stack alignItems='baseline'>
                <Typography color={SdxColors.white} variant='h3'>
                  Product Swap
                </Typography>
                <Slide container={productSwapHomeLinkRef.current} direction='up' in={!!subHeader}>
                  <Typography variant='caption' color='white'>
                    {subHeader}
                  </Typography>
                </Slide>
              </Stack>
            </NavLink>

            {process.env.NODE_ENV !== 'production' && showDevTag && (
              <Chip sx={{ ml: 1 }} label={process.env.NODE_ENV} size='small' color='error' />
            )}

            <Stack
              flexGrow={location.pathname === '/' ? 0 : 1}
              direction='row'
              flex={1}
              alignItems='stretch'
              justifyContent='center'
            >
              <GlobalCatalogSelector />
              <SearchBar />
            </Stack>

            <ExcelDownloadProgress />
            <UserMenu />
          </Stack>
        </Toolbar>
      </AppBarStyled>
      <MainContent>{children}</MainContent>
      <Box
        component='footer'
        style={{
          height: themeVariables.footerHeight,
          padding: 16,
          backgroundColor: SdxColors.white,
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row-reverse',
        }}
      >
        <Typography color={SdxColors.blueDarker} variant='h5'>
          Product Swap v{packageJson.version}
        </Typography>
        {latestPriceUpdateDate && (
          <Typography color={SdxColors.blueDarker} variant='h5'>
            {t('components.footer.latestPriceUpdateDate')}: {latestPriceUpdateDate} &nbsp;
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  borderRadius: '0px !important',

  '&.appBarShift': {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const MainContent = styled('main')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minHeight: `calc(100vh - ${themeVariables.appBarHeight}px - ${themeVariables.footerHeight}px)`,
  marginTop: themeVariables.appBarHeight,
  backgroundColor: Colors.background,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),

  '&.contentShift': {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
}));
